import { lazy, Suspense, useEffect, useState } from "react";
import {
  AdminApiError,
  fetchAdminSubmissionVersion,
  fetchAdminSubmissionVersions,
  fetchAdminSubmissionWorkingCopy,
  getAdminSubmissionMarkdownDownloadPath,
  type AdminSubmissionVersionDetail,
  type AdminSubmissionVersionSourceKind,
  type AdminSubmissionVersionSummary,
  type AdminSubmissionWorkingCopy,
} from "./admin-api";

const SubmissionWorkingDocumentEditor = lazy(() => import("./SubmissionWorkingDocumentEditor").then((module) => ({ default: module.SubmissionWorkingDocumentEditor })));
const SubmissionMarkdownViewer = lazy(() => import("./SubmissionWorkingDocumentEditor").then((module) => ({ default: module.SubmissionMarkdownViewer })));

type DocumentTab = "original" | "working" | "json";

type LoadState<T> = { loading: boolean; error: string | null; value: T | null };

const EMPTY_STATE = { loading: false, error: null, value: null };

export function SubmissionDocumentPanel({ submissionId }: { submissionId: string }) {
  const [versions, setVersions] = useState<LoadState<AdminSubmissionVersionSummary[]>>(EMPTY_STATE);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [detail, setDetail] = useState<LoadState<AdminSubmissionVersionDetail>>(EMPTY_STATE);
  const [workingCopy, setWorkingCopy] = useState<LoadState<AdminSubmissionWorkingCopy>>(EMPTY_STATE);
  const [tab, setTab] = useState<DocumentTab>("original");
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setVersions({ loading: true, error: null, value: null });
    setSelectedVersion(null);
    setDetail(EMPTY_STATE);
    setWorkingCopy(EMPTY_STATE);
    fetchAdminSubmissionVersions(submissionId)
      .then((items) => {
        if (!alive) return;
        setVersions({ loading: false, error: null, value: items });
        setSelectedVersion(items.find((item) => item.isLatest)?.versionNumber ?? items[0]?.versionNumber ?? null);
      })
      .catch((error) => {
        if (alive) setVersions({ loading: false, error: errorMessage(error), value: null });
      });
    return () => { alive = false; };
  }, [submissionId]);

  useEffect(() => {
    if (selectedVersion == null) return;
    let alive = true;
    setDetail({ loading: true, error: null, value: null });
    setWorkingCopy(EMPTY_STATE);
    fetchAdminSubmissionVersion(submissionId, selectedVersion)
      .then((value) => { if (alive) setDetail({ loading: false, error: null, value }); })
      .catch((error) => { if (alive) setDetail({ loading: false, error: errorMessage(error), value: null }); });
    return () => { alive = false; };
  }, [submissionId, selectedVersion]);

  useEffect(() => {
    if (tab !== "working" || selectedVersion == null) return;
    let alive = true;
    setWorkingCopy({ loading: true, error: null, value: null });
    fetchAdminSubmissionWorkingCopy(submissionId, selectedVersion)
      .then((value) => { if (alive) setWorkingCopy({ loading: false, error: null, value }); })
      .catch((error) => { if (alive) setWorkingCopy({ loading: false, error: errorMessage(error), value: null }); });
    return () => { alive = false; };
  }, [submissionId, selectedVersion, tab]);

  const selectedSummary = versions.value?.find((item) => item.versionNumber === selectedVersion) ?? null;
  const copyText = async (text: string, message: string) => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else copyTextWithFallback(text);
      setCopyStatus(message);
    } catch {
      setCopyStatus("브라우저가 복사를 차단했습니다. 내용을 직접 선택해 복사해주세요.");
    }
    window.setTimeout(() => setCopyStatus(null), 1800);
  };

  return (
    <section className="admin-info-section admin-submission-documents" aria-labelledby="admin-submission-documents-heading">
      <div className="admin-document-heading-row">
        <div>
          <h3 id="admin-submission-documents-heading">제출 문서</h3>
          <p className="admin-helper-text">파트너 제출 버전별 원본과 내부 작업용 수정본을 따로 보관합니다.</p>
        </div>
        <label className="admin-version-select">
          <span>파트너 제출 버전</span>
          <select
            value={selectedVersion ?? ""}
            onChange={(event) => setSelectedVersion(Number(event.target.value))}
            disabled={versions.loading || !versions.value?.length}
          >
            {versions.value?.map((version) => (
              <option key={version.id} value={version.versionNumber}>
                {versionOptionLabel(version)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {versions.loading && <div className="admin-empty" role="status">제출 버전을 불러오는 중입니다.</div>}
      {versions.error && <div className="admin-alert error" role="alert">{versions.error}</div>}
      {!versions.loading && !versions.error && versions.value?.length === 0 && <div className="admin-empty">저장된 제출 버전이 없습니다.</div>}

      {selectedSummary && (
        <div className="admin-version-context">
          <strong>{versionOptionLabel(selectedSummary)}</strong>
          <span>{formatDate(selectedSummary.submittedAt)}</span>
          {selectedSummary.revisionReason && <span>수정 요청: {selectedSummary.revisionReason}</span>}
        </div>
      )}

      <div className="admin-document-tabs" role="tablist" aria-label="제출 문서 형식">
        <DocumentTabButton tab="original" current={tab} onSelect={setTab}>파트너 제출 원본</DocumentTabButton>
        <DocumentTabButton tab="working" current={tab} onSelect={setTab}>내부 작업용 수정본</DocumentTabButton>
        <DocumentTabButton tab="json" current={tab} onSelect={setTab}>원본 JSON</DocumentTabButton>
      </div>

      {detail.loading && <div className="admin-empty" role="status">문서를 불러오는 중입니다.</div>}
      {detail.error && <div className="admin-alert error" role="alert">{detail.error}</div>}
      {copyStatus && <div className="admin-alert success" role="status">{copyStatus}</div>}

      {!detail.loading && detail.value && selectedVersion != null && tab === "original" && (
        <FormattedMarkdownPreview
          markdown={detail.value.originalMarkdown}
          downloadPath={getAdminSubmissionMarkdownDownloadPath(submissionId, selectedVersion, "original")}
          onCopy={() => copyText(detail.value!.originalMarkdown, "원본 Markdown을 복사했습니다.")}
        />
      )}

      {!detail.loading && detail.value && selectedVersion != null && tab === "working" && (
        workingCopy.loading ? <div className="admin-empty" role="status">작업용 수정본을 불러오는 중입니다.</div>
          : workingCopy.error ? <div className="admin-alert error" role="alert">{workingCopy.error}</div>
            : workingCopy.value ? (
              <Suspense fallback={<div className="admin-empty" role="status">Markdown 편집기를 불러오는 중입니다.</div>}>
                <SubmissionWorkingDocumentEditor
                  key={`${submissionId}:${selectedVersion}`}
                  submissionId={submissionId}
                  versionNumber={selectedVersion}
                  initialWorkingCopy={workingCopy.value}
                />
              </Suspense>
            ) : null
      )}

      {!detail.loading && detail.value && tab === "json" && (
        <DocumentPreview
          title="선택 버전 원본 JSON"
          description="개발 확인용 데이터입니다. 일반 작업은 원본 Markdown을 이용해주세요."
          markdown={JSON.stringify({ version: { ...detail.value, originalMarkdown: undefined } }, null, 2)}
          onCopy={() => copyText(JSON.stringify({ version: { ...detail.value!, originalMarkdown: undefined } }, null, 2), "선택 버전 JSON을 복사했습니다.")}
          codeLanguage="json"
        />
      )}
    </section>
  );
}

function DocumentTabButton({ tab, current, onSelect, children }: { tab: DocumentTab; current: DocumentTab; onSelect: (tab: DocumentTab) => void; children: string }) {
  const selected = current === tab;
  return <button type="button" role="tab" aria-selected={selected} className={selected ? "active" : ""} onClick={() => onSelect(tab)}>{children}</button>;
}

function FormattedMarkdownPreview({ markdown, downloadPath, onCopy }: { markdown: string; downloadPath: string; onCopy: () => void }) {
  return (
    <div className="admin-document-panel" role="tabpanel">
      <div className="admin-json-header">
        <div><h4>파트너 제출 원본 Markdown</h4><p>제출 당시 응답을 빠짐없이 정리한 읽기 전용 문서입니다. 비밀번호와 접근 토큰은 포함하지 않습니다.</p></div>
        <div className="button-row">
          <button className="button secondary compact" type="button" onClick={onCopy}>복사</button>
          <a className="button secondary compact" href={downloadPath}>다운로드</a>
        </div>
      </div>
      <Suspense fallback={<div className="admin-empty" role="status">Markdown 미리보기를 불러오는 중입니다.</div>}>
        <SubmissionMarkdownViewer markdown={markdown} />
      </Suspense>
    </div>
  );
}

function DocumentPreview({ title, description, markdown, downloadPath, onCopy, codeLanguage = "markdown" }: {
  title: string;
  description: string;
  markdown: string;
  downloadPath?: string;
  onCopy: () => void;
  codeLanguage?: "markdown" | "json";
}) {
  return (
    <div className="admin-document-panel" role="tabpanel">
      <div className="admin-json-header">
        <div><h4>{title}</h4><p>{description}</p></div>
        <div className="button-row">
          <button className="button secondary compact" type="button" onClick={onCopy}>복사</button>
          {downloadPath && <a className="button secondary compact" href={downloadPath}>다운로드</a>}
        </div>
      </div>
      <pre className={`admin-document-preview language-${codeLanguage}`}>{markdown}</pre>
    </div>
  );
}

function versionOptionLabel(version: AdminSubmissionVersionSummary): string {
  const kind = sourceKindLabel(version.sourceKind);
  return `${version.versionNumber}차 · ${kind}${version.isLatest ? " · 최신" : ""}`;
}

function sourceKindLabel(kind: AdminSubmissionVersionSourceKind): string {
  if (kind === "INITIAL") return "최초 제출";
  if (kind === "PARTNER_REVISION") return "수정 제출";
  return "도입 시점 기준본";
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "medium", timeStyle: "short" }).format(date);
}

function copyTextWithFallback(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function errorMessage(error: unknown): string {
  if (error instanceof AdminApiError) return error.message;
  return error instanceof Error ? error.message : "제출 문서를 불러오지 못했습니다.";
}
